import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Modal as AntdModal,
  Button,
  Select,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import tw from 'twin.macro';

import Modal from '@/components/Modal';
import { MENU_GROUPS, USER_PERMISSION_USAGE_TYPE } from '@/constants';
import { useUpdateAdminUserRolePermissions } from '@/hooks/mutations/admin';
import { useAdminPermissions, useAdminUserRoles } from '@/hooks/queries/admin';
import { useMe } from '@/hooks/queries/universal';
import type { AdminUserPermission, GetAdminUserResponse } from '@/types/admin';
import { getQueryKey } from '@/utils/queryKeys';
import { getApiErrorMessages } from '@/utils/user';

type RowState = 'KEPT' | 'ADDED' | 'REMOVED';

interface Row extends AdminUserPermission {
  state: RowState;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  role: NonNullable<GetAdminUserResponse['userRole']>;
  /** 상세 API 가 내려준 현재 매핑 (모달을 열 때의 기준값) */
  permissions: AdminUserPermission[];
}

/** 이 권한을 빼면 유저 관리 화면 자체에 못 들어온다 */
const SELF_LOCKOUT_CODE = 'USER_MANAGE';

const groupLabel = (divider: string) =>
  MENU_GROUPS[divider as keyof typeof MENU_GROUPS]?.label ?? divider;

const usageLabel = (usageType: AdminUserPermission['usageType']) =>
  USER_PERMISSION_USAGE_TYPE[usageType] ?? usageType;

const byOrder = (a: AdminUserPermission, b: AdminUserPermission) =>
  a.order - b.order || a.id - b.id;

const RolePermissionModal = ({ isOpen, onClose, role, permissions }: Props) => {
  const queryClient = useQueryClient();
  const editable = !role.isSuperRole;

  const allPermissions = useAdminPermissions({ enabled: isOpen });
  const roles = useAdminUserRoles({ enabled: isOpen });
  const me = useMe();

  const initialIds = useMemo(
    () => permissions.map(({ id }) => id),
    [permissions]
  );
  const [draftIds, setDraftIds] = useState<number[]>(initialIds);

  useEffect(() => {
    if (isOpen) {
      setDraftIds(initialIds);
    }
  }, [isOpen, initialIds]);

  const permissionById = useMemo(() => {
    const map = new Map<number, AdminUserPermission>();
    // 전체 목록을 못 불러와도 현재 매핑은 보여줄 수 있게 상세 응답을 먼저 넣는다
    permissions.forEach((permission) => map.set(permission.id, permission));
    (allPermissions.data ?? []).forEach((permission) =>
      map.set(permission.id, permission)
    );
    return map;
  }, [permissions, allPermissions.data]);

  const initialSet = useMemo(() => new Set(initialIds), [initialIds]);
  const draftSet = useMemo(() => new Set(draftIds), [draftIds]);

  const rows = useMemo<Row[]>(() => {
    const ids = Array.from(new Set([...initialIds, ...draftIds]));

    return ids
      .map((id) => permissionById.get(id))
      .filter((permission): permission is AdminUserPermission => !!permission)
      .sort(byOrder)
      .map((permission) => {
        let state: RowState = 'KEPT';
        if (!initialSet.has(permission.id)) state = 'ADDED';
        else if (!draftSet.has(permission.id)) state = 'REMOVED';

        return { ...permission, state };
      });
  }, [initialIds, draftIds, permissionById, initialSet, draftSet]);

  const addedCount = rows.filter((row) => row.state === 'ADDED').length;
  const removedCount = rows.filter((row) => row.state === 'REMOVED').length;
  const changed = addedCount + removedCount > 0;

  const addOptions = useMemo(
    () =>
      (allPermissions.data ?? [])
        .filter(({ id }) => !draftSet.has(id))
        .sort(byOrder)
        .map((permission) => ({
          value: permission.id,
          // 검색은 이 문자열로 한다
          label: `${groupLabel(permission.divider)} ${permission.category} ${permission.name} ${permission.code} ${usageLabel(permission.usageType)}`,
          permission,
        })),
    [allPermissions.data, draftSet]
  );

  const roleUserCount =
    roles.data?.results.find(({ id }) => id === role.id)?.userCount ?? null;
  const isMyRole = !!me.data && me.data.userRole === role.name;

  const updateRolePermissions = useUpdateAdminUserRolePermissions({
    onSuccess: () => {
      message.success('역할 권한을 저장했어요');
      queryClient.invalidateQueries({ queryKey: getQueryKey('admin:getUser') });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getUsers'),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getUserRoles'),
      });
      // 내 역할이면 사이드바 메뉴가 바뀔 수 있다
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getMyPermissions'),
      });
      onClose();
    },
    onError: (error) => {
      message.error(getApiErrorMessages(error).message);
    },
  });

  const save = () => {
    const removesOwnAccess =
      isMyRole &&
      rows.some(
        (row) => row.state === 'REMOVED' && row.code === SELF_LOCKOUT_CODE
      );

    AntdModal.confirm({
      centered: true,
      title: `${role.name} 역할 권한을 저장할까요?`,
      content: (
        <div css={tw`flex flex-col gap-[8px]`}>
          <Typography.Text>
            추가 {addedCount}개 · 삭제 {removedCount}개
          </Typography.Text>
          <Typography.Text type="secondary">
            {roleUserCount !== null
              ? `이 역할을 쓰는 ${roleUserCount}명에게 바로 적용돼요.`
              : '이 역할을 쓰는 모든 유저에게 바로 적용돼요.'}
          </Typography.Text>
          {removesOwnAccess && (
            <Typography.Text type="danger">
              지금 로그인한 계정의 역할에서 유저 관리 권한을 빼요. 저장하면 이
              화면에 다시 들어올 수 없어요.
            </Typography.Text>
          )}
        </div>
      ),
      okText: '저장',
      cancelText: '취소',
      okButtonProps: { danger: removesOwnAccess },
      onOk: () =>
        updateRolePermissions.mutateAsync({
          id: role.id,
          permissionIds: draftIds,
        }),
    });
  };

  return (
    <Modal
      open={isOpen}
      width={920}
      maskClosable={!changed}
      onCancel={() => {
        if (!updateRolePermissions.isPending) onClose();
      }}
      title={`권한 관리 · ${role.name}`}
      footer={[
        <Button key="close" onClick={onClose}>
          닫기
        </Button>,
        editable && (
          <Button
            key="save"
            type="primary"
            disabled={!changed}
            loading={updateRolePermissions.isPending}
            onClick={save}
          >
            저장
          </Button>
        ),
      ]}
    >
      <div css={tw`flex flex-col gap-[16px] mt-[16px]`}>
        {editable ? (
          <Alert
            type="info"
            showIcon
            message={`권한은 역할(${role.name}) 단위로 매핑돼요. 저장하면 ${
              roleUserCount !== null
                ? `이 역할을 쓰는 ${roleUserCount}명`
                : '이 역할을 쓰는 모든 유저'
            }에게 함께 적용돼요.`}
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="최고 관리자 역할은 수정할 수 없어요. 매핑과 무관하게 모든 메뉴 · API 권한을 가져요."
          />
        )}

        {editable && (
          <div css={tw`w-full [.ant-select]:w-full`}>
            <Select<number | null>
              showSearch
              value={null}
              placeholder="추가할 권한을 검색하세요 (메뉴 그룹 · 카테고리 · 권한명 · 코드)"
              optionFilterProp="label"
              loading={allPermissions.isFetching}
              options={addOptions}
              notFoundContent="추가할 수 있는 권한이 없어요"
              optionRender={({ data }) => (
                <div
                  css={tw`flex flex-row items-center justify-between gap-[8px]`}
                >
                  <span>
                    <Typography.Text type="secondary">
                      {groupLabel(data.permission.divider)} ·{' '}
                      {data.permission.category}
                    </Typography.Text>{' '}
                    {data.permission.name}{' '}
                    <Typography.Text code>
                      {data.permission.code}
                    </Typography.Text>
                  </span>
                  <Tag
                    css={tw`m-0`}
                    color={
                      data.permission.usageType === 'MENU' ? 'cyan' : 'default'
                    }
                  >
                    {usageLabel(data.permission.usageType)}
                  </Tag>
                </div>
              )}
              onSelect={(permissionId) => {
                if (permissionId !== null) {
                  setDraftIds((prev) => [...prev, permissionId]);
                }
              }}
            />
          </div>
        )}

        <div css={tw`flex flex-row items-center justify-between`}>
          <Typography.Text>
            총 {draftIds.length}개
            {changed && (
              <Typography.Text type="secondary">
                {' '}
                (추가 {addedCount} · 삭제 {removedCount})
              </Typography.Text>
            )}
          </Typography.Text>
          {editable && (
            <Button
              type="link"
              size="small"
              disabled={!changed}
              onClick={() => setDraftIds(initialIds)}
            >
              변경 취소
            </Button>
          )}
        </div>

        <Table<Row>
          size="small"
          rowKey="id"
          pagination={false}
          scroll={{ y: 360 }}
          dataSource={rows}
          locale={{ emptyText: '매핑된 권한이 없어요' }}
          columns={[
            {
              dataIndex: 'divider',
              title: '메뉴 그룹',
              width: 110,
              render: (value: string) => groupLabel(value),
            },
            { dataIndex: 'category', title: '카테고리', width: 120 },
            {
              dataIndex: 'name',
              title: '권한명',
              render: (value: string, row) => (
                <Typography.Text
                  delete={row.state === 'REMOVED'}
                  type={row.state === 'REMOVED' ? 'secondary' : undefined}
                >
                  {value}
                </Typography.Text>
              ),
            },
            {
              dataIndex: 'code',
              title: '코드',
              width: 200,
              render: (value: string) => (
                <Typography.Text code>{value}</Typography.Text>
              ),
            },
            {
              dataIndex: 'usageType',
              title: '유형',
              width: 80,
              align: 'center',
              render: (_, { usageType }) => (
                <Tag
                  css={tw`m-0`}
                  color={usageType === 'MENU' ? 'cyan' : 'default'}
                >
                  {usageLabel(usageType)}
                </Tag>
              ),
            },
            {
              dataIndex: 'state',
              title: '변경',
              width: 80,
              align: 'center',
              render: (state: RowState) => {
                if (state === 'ADDED') {
                  return (
                    <Tag css={tw`m-0`} color="green">
                      추가
                    </Tag>
                  );
                }
                if (state === 'REMOVED') {
                  return (
                    <Tag css={tw`m-0`} color="red">
                      삭제
                    </Tag>
                  );
                }
                return '-';
              },
            },
            ...(editable
              ? [
                  {
                    key: 'action',
                    title: '',
                    width: 90,
                    align: 'center' as const,
                    render: (_: unknown, row: Row) =>
                      row.state === 'REMOVED' ? (
                        <Button
                          size="small"
                          type="link"
                          onClick={() =>
                            setDraftIds((prev) => [...prev, row.id])
                          }
                        >
                          되돌리기
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() =>
                            setDraftIds((prev) =>
                              prev.filter((id) => id !== row.id)
                            )
                          }
                        >
                          {row.state === 'ADDED' ? '취소' : '삭제'}
                        </Button>
                      ),
                  },
                ]
              : []),
          ]}
        />
      </div>
    </Modal>
  );
};

export default RolePermissionModal;
